import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ShieldOff, ShieldBan } from 'lucide-react';

import { securityApi } from '../../../api/security.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { t } from '../../../lib/i18n.js';

// Render a remaining ban TTL (seconds) as a compact human readable duration.
function formatTtl(sec) {
  if (sec == null) return 'senza scadenza';
  if (sec <= 0) return 'in scadenza';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}g`);
  if (h) parts.push(`${h}h`);
  if (m && !d) parts.push(`${m}m`);
  if (!parts.length) parts.push(`${sec}s`);
  return parts.join(' ');
}

function BanModal({ open, onClose, onSubmit, loading }) {
  const [ip, setIp] = useState('');
  const [duration, setDuration] = useState('');

  const submit = () => {
    const body = { ip: ip.trim() };
    if (duration.trim()) body.durationSec = Number(duration);
    onSubmit(body);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Blocca un indirizzo IP"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} loading={loading} disabled={!ip.trim()}>Blocca</Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Indirizzo IP"
          value={ip}
          onChange={(e) => setIp(e.target.value)}
          placeholder="es. 203.0.113.42"
          hint="IPv4 o IPv6."
        />
        <Input
          label="Durata (secondi)"
          type="number"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          placeholder="vuoto = durata predefinita"
          hint="Lascia vuoto per usare la prima durata del ladder configurato."
        />
      </div>
    </Modal>
  );
}

export function SecurityPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('security:manage');
  const queryClient = useQueryClient();

  const bansQuery = useQuery({
    queryKey: ['security', 'bans'],
    queryFn: securityApi.listBans,
    enabled: canManage,
    refetchInterval: 30_000
  });

  const [banModal, setBanModal] = useState(false);
  const [unbanTarget, setUnbanTarget] = useState(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['security', 'bans'] });

  const banMutation = useMutation({
    mutationFn: (body) => securityApi.banIp(body),
    onSuccess: () => { toast.success('IP bloccato.'); invalidate(); setBanModal(false); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const unbanMutation = useMutation({
    mutationFn: (ip) => securityApi.unbanIp(ip),
    onSuccess: () => { toast.success('IP sbloccato.'); invalidate(); setUnbanTarget(null); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  if (!canManage) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Sicurezza" />
        <Card><p className="text-sm text-ink-muted">Nessun permesso di gestione della sicurezza.</p></Card>
      </div>
    );
  }

  const columns = [
    { key: 'ip', header: 'Indirizzo IP', render: (r) => <code className="text-xs text-ink">{r.ip}</code> },
    { key: 'type', header: 'Tipo', render: (r) => (
      <span className="flex flex-wrap gap-1">
        {r.hard && <Badge tone="danger">Ban</Badge>}
        {r.shadow && <Badge tone="warning">Shadow</Badge>}
        {!r.hard && !r.shadow && <span className="text-ink-faint">-</span>}
      </span>
    ) },
    { key: 'ttlSec', header: 'Scade tra', render: (r) => <span className="text-xs text-ink-muted">{formatTtl(r.ttlSec)}</span> },
    { key: 'score', header: 'Score', align: 'right', render: (r) => <span className="text-xs text-ink-muted">{r.score ?? '-'}</span> },
    { key: 'banCount', header: 'Ban ripetuti', align: 'right', render: (r) => <span className="text-xs text-ink-muted">{r.banCount || 0}</span> },
    { key: 'actions', header: '', align: 'right', render: (r) => (
      <button
        type="button"
        aria-label={`Sblocca ${r.ip}`}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-success/10 hover:text-success"
        onClick={() => setUnbanTarget(r)}
      >
        <ShieldOff className="h-3.5 w-3.5" aria-hidden="true" />
        Sblocca
      </button>
    ) }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Sicurezza"
        description="Indirizzi IP bloccati dal threat guard. I blocchi scadono da soli; qui puoi sbloccare o aggiungere un blocco manuale."
        actions={<Button onClick={() => setBanModal(true)}><ShieldBan className="h-4 w-4" /> Blocca IP</Button>}
      />

      <DataTable
        columns={columns}
        rows={bansQuery.data?.items || []}
        loading={bansQuery.isLoading}
        empty={{ title: 'Nessun IP bloccato', description: 'Nessun indirizzo risulta attualmente bannato o in shadow ban.' }}
      />

      <BanModal
        open={banModal}
        onClose={() => setBanModal(false)}
        onSubmit={(body) => banMutation.mutate(body)}
        loading={banMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(unbanTarget)}
        title="Sbloccare l'indirizzo?"
        description={unbanTarget ? `Il blocco su "${unbanTarget.ip}" sarà rimosso (ban, shadow ban, score e conteggio). L'indirizzo potrà di nuovo raggiungere le API.` : ''}
        confirmLabel="Sblocca"
        loading={unbanMutation.isPending}
        onCancel={() => setUnbanTarget(null)}
        onConfirm={() => unbanMutation.mutate(unbanTarget.ip)}
      />
    </div>
  );
}
